import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import moment from 'moment-timezone';

import Screen from '../components/Screen';
import Dropdown from '../components/Dropdown';
import colors from '../config/colors';
import keepConnectApi from '../api/keepConnectsApi';
import useApi from '../hooks/useApi';
import { FlatList } from '../components/PlatformWrapperFlatlistFromGestureHandler';
import { EventItem } from '../components/deviceViews/pumpPal';

/**
 * PumpPalEventsScreen - Full event history for PumpPal devices
 * Shows all pump runs, alerts, and system events with filtering
 */
function PumpPalEventsScreen({ route, navigation }) {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedDays, setSelectedDays] = useState(7);
    const [selectedFilter, setSelectedFilter] = useState('all');

    const getPumpPalRunsApi = useApi(keepConnectApi.getPumpPalRuns);
    const getPumpPalEventsApi = useApi(keepConnectApi.getPumpPalEvents);

    const deviceMac = route.params.item.MAC;
    const deviceName = route.params.item.Name;

    const dayOptions = [
        { label: 'Last 7 days', value: 7 },
        { label: 'Last 14 days', value: 14 },
        { label: 'Last 30 days', value: 30 },
    ];

    const filterOptions = [
        { label: 'All Events', value: 'all' },
        { label: 'Pump Runs Only', value: 'runs' },
        { label: 'Alerts Only', value: 'alerts' },
        { label: 'System Events', value: 'system' },
    ];

    // Map alertType code from PumpRuns table to event type
    const mapAlertTypeFromCode = (alertType) => {
        const code = parseInt(alertType) || 0;
        switch (code) {
            case 1: return 'dry_run';
            case 2: return 'high_power';
            case 3: return 'excessive_run';
            case 4: return 'short_cycling';
            case 5: return 'unusual_power';
            case 6: return 'excessive_run';
            default: return 'pump_finished';
        }
    };

    // Map Events table param to event type
    const mapParamToEventType = (param, direction) => {
        if (!param) return 'unusual_power';
        const paramLower = param.toLowerCase();
        const dir = parseInt(direction) || 0;
        if (paramLower.includes('palt')) return 'unusual_power';
        if (paramLower.includes('psc')) return 'short_cycling';
        if (paramLower.includes('pavga') || paramLower.includes('current')) {
            return dir === 0 ? 'dry_run' : 'high_power';
        }
        if (paramLower.includes('prt') || paramLower.includes('runtime')) return 'excessive_run';
        return 'unusual_power';
    };

    // Map Resets table reason to event type
    const mapReasonToEventType = (reason) => {
        if (!reason) return 'device_event';
        const reasonLower = reason.toLowerCase();
        if (reasonLower.includes('power on') || reasonLower.includes('boot')) return 'device_boot';
        if (reasonLower.includes('offline')) return 'device_offline';
        if (reasonLower.includes('online')) return 'device_online';
        return 'device_event';
    };

    const loadEvents = async () => {
        setIsLoading(true);
        const eventList = [];

        // Fetch pump runs
        // NOTE: Alerts are shown from Events table only - PumpRuns always show as pump_finished
        // to avoid duplicate alert entries (same alert appears in both tables)
        const runsResponse = await getPumpPalRunsApi.request("\"" + deviceMac + "\"", selectedDays, 100, 0);
        if (runsResponse.data && runsResponse.data.length > 0) {
            for (const run of runsResponse.data) {
                const runTime = moment.utc(run.startTime).local();
                const timestamp = runTime.fromNow();
                eventList.push({
                    id: `run-${run.id}`,
                    type: 'pump_finished', // Always show as finished - alerts come from Events table
                    timestamp: timestamp,
                    duration: Math.round(Number(run.durationSec) || 0),
                    power: Math.round(Number(run.avgPower) || 0),
                    current: Number(run.avgCurrent)?.toFixed(2),
                    isAlert: false, // Never mark as alert from PumpRuns
                    sortTime: runTime.valueOf(),
                    category: 'runs', // Always categorize as runs
                });
            }
        }

        // Fetch system events (Events + Resets)
        const eventsResponse = await getPumpPalEventsApi.request("\"" + deviceMac + "\"", selectedDays, 100);
        if (eventsResponse.data && eventsResponse.data.length > 0) {
            for (const evt of eventsResponse.data) {
                const evtTime = moment.utc(evt.time).local();
                const timestamp = evtTime.fromNow();
                if (evt.source === 'event') {
                    eventList.push({
                        id: `evt-${evt.time}`,
                        type: mapParamToEventType(evt.param, evt.direction),
                        timestamp: timestamp,
                        isAlert: true,
                        sortTime: evtTime.valueOf(),
                        category: 'alerts',
                    });
                } else if (evt.source === 'reset') {
                    eventList.push({
                        id: `rst-${evt.time}`,
                        type: mapReasonToEventType(evt.reason),
                        timestamp: timestamp,
                        isAlert: false,
                        sortTime: evtTime.valueOf(),
                        category: 'system',
                    });
                }
            }
        }

        // Sort all events by timestamp (most recent first)
        eventList.sort((a, b) => b.sortTime - a.sortTime);

        setEvents(eventList);
        setIsLoading(false);
        setRefreshing(false);
    };

    useEffect(() => {
        loadEvents();
    }, [selectedDays]);

    const handleRefresh = () => {
        setRefreshing(true);
        loadEvents();
    };

    // Filter events based on selected filter
    const filteredEvents = events.filter(event => {
        if (selectedFilter === 'all') return true;
        if (selectedFilter === 'runs') return event.type === 'pump_finished' || event.category === 'runs';
        if (selectedFilter === 'alerts') return event.isAlert || event.category === 'alerts';
        if (selectedFilter === 'system') return event.category === 'system';
        return true;
    });

    const renderEventItem = ({ item }) => (
        <EventItem
            type={item.type}
            timestamp={item.timestamp}
            duration={item.duration}
            power={item.power}
            current={item.current}
            isAlert={item.isAlert}
        />
    );

    return (
        <Screen>
            <View style={styles.header}>
                <Text style={styles.title}>{deviceName}</Text>
                <Text style={styles.subtitle}>Event History</Text>
            </View>

            <View style={styles.filterRow}>
                <View style={styles.filterContainer}>
                    <Dropdown
                        items={dayOptions}
                        onSelectItem={(item) => setSelectedDays(item.value)}
                        selectedItem={dayOptions.find(d => d.value === selectedDays)}
                        placeholder="Select days"
                    />
                </View>
                <View style={styles.filterContainer}>
                    <Dropdown
                        items={filterOptions}
                        onSelectItem={(item) => setSelectedFilter(item.value)}
                        selectedItem={filterOptions.find(f => f.value === selectedFilter)}
                        placeholder="Filter"
                    />
                </View>
            </View>

            <View style={styles.statsRow}>
                <Text style={styles.statsText}>
                    Showing {filteredEvents.length} events
                </Text>
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={styles.loadingText}>Loading events...</Text>
                </View>
            ) : filteredEvents.length === 0 ? (
                <View style={styles.emptyContainer}>
                    <Text style={styles.emptyText}>No events found</Text>
                </View>
            ) : (
                <FlatList
                    data={filteredEvents}
                    keyExtractor={(item) => item.id}
                    renderItem={renderEventItem}
                    refreshing={refreshing}
                    onRefresh={handleRefresh}
                    contentContainerStyle={styles.listContent}
                />
            )}
        </Screen>
    );
}

const styles = StyleSheet.create({
    header: {
        padding: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
    },
    title: {
        fontSize: 18,
        fontWeight: 'bold',
        color: colors.white,
    },
    subtitle: {
        fontSize: 14,
        color: colors.white,
        opacity: 0.9,
        marginTop: 4,
    },
    filterRow: {
        flexDirection: 'row',
        padding: 12,
        backgroundColor: colors.light,
    },
    filterContainer: {
        flex: 1,
        marginHorizontal: 4,
    },
    statsRow: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        backgroundColor: colors.white,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    statsText: {
        fontSize: 13,
        color: colors.medium,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    loadingText: {
        marginTop: 12,
        fontSize: 14,
        color: colors.medium,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyText: {
        fontSize: 16,
        color: colors.medium,
    },
    listContent: {
        paddingBottom: 20,
    },
});

export default PumpPalEventsScreen;
